import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, ClipboardCheck, Loader2, MessageSquare, MessageSquarePlus, Minus, RefreshCw, Send, Truck, Wrench, X, Play } from "lucide-react";
import API from "../services/api";
import { getPosition } from "../services/native";
import {
  ITEM_NOTE_MAX, cachedShift, deviceDate, forgetCachedCheck, nzClock, replaceCachedCheck,
} from "../services/timeclock";

/* The NZTA "Daily Checklist - Goods Service Vehicles" (Dczl/0513) on the
 * phone. Same paper, same 30 items in the same order, one sheet per truck per
 * day - the office prints it back out looking like the form the driver knows.
 *
 * Three things the operator decided that shape this screen:
 *
 *   1. It is REQUIRED before a driver starts a shift. Start shift opens this,
 *      and the shift starts when it is submitted. Offsiders never see it -
 *      they do not drive - and DriverApp does not open it for them.
 *   2. A fault WARNS and is RECORDED. It blocks nothing: a cracked mirror
 *      must not strand a run, the office decides. So Fault is one tap like
 *      OK is, and the only consequence here is a "what's wrong?" line under
 *      the item and an amber card.
 *   3. Drivers do this 250 times a year. "All OK" is one tap, not 30, and
 *      the last truck is remembered because most drivers take the same one.
 *   4. The paper form's line is "Faults/Comments" and the operator wants it
 *      used that way: the comments box is always there, not only when
 *      something is crossed, so a suggestion ("we need new straps") has a
 *      place to go. A driver can add to today's sheet later in the day.
 *
 * Nothing here needs signal after the first open: the truck list and the
 * template are cached, the submitted check is queued by timeclock.js and
 * sent when the phone next sees the office. The one exception is a comment
 * added AFTER submitting: it goes straight to the office or not at all - the
 * queue is for punches and checks, and the driver is told to try on signal.
 */

const TRUCKS_KEY = "dav_trucks";
const TEMPLATE_KEY = "dav_check_template";
const LAST_TRUCK_KEY = "dav_last_truck";

/* The 30 items of the paper form, in its order. This is the FALLBACK for a
 * phone that has never reached /v1/checks/template - the server's copy wins
 * whenever it has been seen, so an item changed at the office reaches the cab
 * on the next online open without a release. Keys are the contract's; the
 * server refuses a check that is missing any of them. */
const DEFAULT_TEMPLATE = {
  groups: [
    { key: "fluid", label: "Fluid levels", items: [
      { key: "engine_oil", label: "Engine Oil" },
      { key: "power_steering", label: "Power Steering" },
      { key: "cooling_water", label: "Cooling Water" },
    ] },
    { key: "exterior", label: "Exterior", items: [
      { key: "load_security", label: "Load Security" },
      { key: "load_anchors", label: "Load Anchors" },
      { key: "mirrors", label: "Mirrors" },
      { key: "doors_locks", label: "Doors / Locks" },
      { key: "mudguards_flaps", label: "M.Guards/Flaps" },
      { key: "drain_air_tanks", label: "Drain Air Tanks" },
      { key: "tyres_wheels", label: "Tyres / Wheels", critical: true },
      { key: "body_deck_security", label: "Body/Deck Security" },
      { key: "tow_connections", label: "Tow Connections" },
      { key: "cab_release_lock", label: "Cab Release / Lock" },
      { key: "fuel_tank_cap", label: "Fuel Tank / Cap" },
    ] },
    { key: "lights", label: "Lights", items: [
      { key: "front_lights", label: "All Front Lights", critical: true },
      { key: "rear_lights", label: "All Rear Lights", critical: true },
      { key: "side_mark_lights", label: "Side Mark Lights" },
      { key: "other_lights", label: "Other Lights" },
    ] },
    { key: "interior", label: "Interior", items: [
      { key: "windscreen", label: "Windscreen" },
      { key: "washers_wipers", label: "Washers/Wipers" },
      { key: "low_air_buzzer", label: "Low Air Buzzer" },
      { key: "gauges", label: "All Gauges" },
      { key: "seat_seatbelt", label: "Seat / Seat Belt" },
      { key: "horn", label: "Horn" },
      { key: "steering", label: "Steering", critical: true },
    ] },
    { key: "brakes", label: "Brakes", items: [
      { key: "park_brake", label: "Park Brake", critical: true },
      { key: "brake_pedal_feel", label: "Brake Pedal Feel", critical: true },
      { key: "leaks_air_fluid", label: "Leaks-Air/Fluid", critical: true },
      { key: "brake_operation", label: "Operation", critical: true },
    ] },
  ],
};

// The header fields of the paper form, named the way the office says them.
const EXPIRY_LABELS = {
  cof_expiry: "COF", licence_expiry: "Rego", ruc_expiry: "RUC", ruc_expiry_km: "RUC", service_due: "Service",
  next_service_date: "Service",
};

/* ------------------------------------------------------------------ *
 * caches (localStorage) - shared with DriverApp
 * ------------------------------------------------------------------ */

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
}
function writeJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage off */ }
}

export function cachedTrucks() {
  const v = readJson(TRUCKS_KEY);
  return Array.isArray(v) ? v : [];
}

export function cachedTemplate() {
  const v = readJson(TEMPLATE_KEY);
  return Array.isArray(v?.groups) && v.groups.length ? v : DEFAULT_TEMPLATE;
}

/** The truck this phone last checked - pre-selected next time. */
export function lastTruckId() {
  try { return localStorage.getItem(LAST_TRUCK_KEY) || ""; } catch { return ""; }
}

/** Plate for a truck id from the cached fleet list, or "" - for the shift
 *  bar when the server's shift carries an id but no plate. */
export function plateFor(truckId) {
  if (!truckId) return "";
  const t = cachedTrucks().find((x) => String(x.id) === String(truckId));
  return t?.license_plate || "";
}

/* /v1/trucks is a bare array today; accept a {trucks:[...]} wrapper too so a
 * change of shape on the office side cannot empty the picker. */
function trucksFrom(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.trucks)) return data.trucks;
  return null;
}

/** Fetch and cache the fleet list and the checklist template. Never throws:
 *  called on every online open so the form works in the yard with no signal.
 *  Returns the best copy there is of each. */
export async function warmPreCheckCache(api = API) {
  let trucks = null;
  let template = null;
  try {
    trucks = trucksFrom((await api.get("/trucks"))?.data);
    if (trucks) writeJson(TRUCKS_KEY, trucks);
  } catch { /* no signal or no auth yet - the cache stands */ }
  try {
    const data = (await api.get("/checks/template"))?.data;
    if (Array.isArray(data?.groups) && data.groups.length) {
      template = data;
      writeJson(TEMPLATE_KEY, template);
    }
  } catch { /* same */ }
  return { trucks: trucks || cachedTrucks(), template: template || cachedTemplate() };
}

/* ------------------------------------------------------------------ *
 * pieces
 * ------------------------------------------------------------------ */

function truckTitle(t) {
  if (!t) return "";
  const bits = [t.license_plate || "(no plate)"];
  if (t.fleet_no) bits.push(`Fleet ${t.fleet_no}`);
  return bits.join(" · ");
}

/* Expired in red, due within 30 days in amber. The server decides what is
 * worth warning about (expiry_warnings); days is preferred and the date is
 * the fallback for an older server that sends only that. */
function ExpiryWarnings({ truck }) {
  const warns = Array.isArray(truck?.expiry_warnings) ? truck.expiry_warnings : [];
  if (!warns.length) return null;
  const today = deviceDate();
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {warns.map((w, i) => {
        const days = w.days == null ? null : Number(w.days);
        const isKm = w.km_left != null;
        const expired = isKm ? w.level === "expired" : days != null ? days < 0 : (w.date && String(w.date) < today);
        const label = EXPIRY_LABELS[w.field] || w.field || "Expiry";
        const when = isKm
          ? (expired ? `ran out ${Math.abs(w.km_left).toLocaleString("en-NZ")} km ago` : `${Number(w.km_left).toLocaleString("en-NZ")} km left`)
          : expired
            ? `expired${w.date ? ` ${w.date}` : ""}`
            : days === 0 ? "expires today"
              : days != null ? `due in ${days} day${days === 1 ? "" : "s"}`
                : `due ${w.date || ""}`;
        return (
          <span key={i}
                className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                  expired ? "bg-red-500/15 text-red-300 border-red-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
            {label} {when}
          </span>
        );
      })}
    </div>
  );
}

/* The paper form's vehicle header, shown to the driver on the truck they
 * picked - make, fleet no, TSL, service due, and the COF / rego / RUC /
 * service dates. Dates take their colour from the server's expiry_warnings
 * (red expired, amber due), so the cab and the office read the same thing.
 * A dash means the office has not entered it yet, not that it is fine. */
const HEADER_TEXT = [["make", "Make"], ["fleet_no", "Fleet No"], ["tsl", "TSL"], ["service_due", "Service due"]];
const HEADER_DATES = [["cof_expiry", "COF expiry"], ["licence_expiry", "Rego expiry"], ["next_service_date", "Next service date"]];
// distance licences: RUC runs out at a hubo reading, not on a date
const HEADER_KM = [["ruc_expiry_km", "RUC runs out at (km on hubo)"], ["next_service_km", "Next service at (km on hubo)"]];
const SNOOZE_DAYS = 7;
const snoozeKey = (id) => `dav_hdr_snooze_${id}`;

/* The driver fills the header in from the cab - the COF, rego and RUC
 * labels are on the windscreen in front of them. The form opens by itself
 * when something is blank or a date is due (it is the driver who will see
 * the renewed label first), and stays folded otherwise, so nobody is asked
 * again until a date comes due. "Skip for now" rests it a week on this
 * phone. Saves go straight to the office; there is no offline queue for
 * this - a wrong expiry date arriving hours later would mislead. */
function VehicleHeader({ truck, onSaved }) {
  const warnByField = {};
  for (const w of (Array.isArray(truck?.expiry_warnings) ? truck.expiry_warnings : [])) {
    if (w?.field) warnByField[w.field] = w;
  }
  const missing = truck
    ? [...HEADER_TEXT, ...HEADER_DATES, HEADER_KM[0]].filter(([k]) => k !== "service_due" && !truck[k]).map(([, l]) => l.replace(/ \(.*\)$/, ""))
    : [];
  const dueLabels = truck ? Object.values(warnByField).map((w) => EXPIRY_LABELS[w.field] || w.field) : [];
  const snoozed = (() => {
    try { return Number(localStorage.getItem(snoozeKey(truck?.id)) || 0) > Date.now(); } catch { return false; }
  })();
  const needsAttention = !!truck && (missing.length > 0 || dueLabels.length > 0);

  const [editing, setEditing] = useState(needsAttention && !snoozed);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedNote, setSavedNote] = useState("");

  const blank = (id) => {
    const d = {};
    for (const [k] of [...HEADER_TEXT, ...HEADER_DATES]) d[k] = truck?.[k] ? String(truck[k]).slice(0, k.endsWith("_date") || k.endsWith("_expiry") ? 10 : 64) : "";
    for (const [k] of HEADER_KM) d[k] = truck?.[k] ? String(truck[k]) : "";
    return d;
  };
  // a different truck picked -> a fresh draft and the open/closed decision again
  const [draftFor, setDraftFor] = useState(null);
  if (truck && draftFor !== truck.id) {
    setDraftFor(truck.id);
    setDraft(blank(truck.id));
    setEditing(needsAttention && !snoozed);
    setErr("");
    setSavedNote("");
  }
  if (!truck) return null;

  const tone = (field) => {
    const w = warnByField[field];
    if (!w) return "text-slate-200";
    const days = w.days == null ? null : Number(w.days);
    return (days != null ? days < 0 : w.level === "expired") ? "text-red-300 font-bold" : "text-amber-300 font-semibold";
  };
  const date = (field) => {
    const v = truck[field];
    if (!v) return "—";
    const [y, m, d] = String(v).slice(0, 10).split("-");
    return y && m && d ? `${d}/${m}/${y}` : String(v);
  };
  const km = truck.next_service_km ? `${Number(truck.next_service_km).toLocaleString("en-NZ")} km` : null;
  const service = [truck.next_service_date ? date("next_service_date") : null, km].filter(Boolean).join(" · ") || "—";
  const rows = [
    ["Make", truck.make || "—", ""], ["Fleet No", truck.fleet_no || "—", ""],
    ["TSL", truck.tsl || "—", ""], ["Service due", truck.service_due || "—", ""],
    ["COF", date("cof_expiry"), tone("cof_expiry")], ["Rego", date("licence_expiry"), tone("licence_expiry")],
    ["RUC", truck.ruc_expiry_km ? `${Number(truck.ruc_expiry_km).toLocaleString("en-NZ")} km` : "—", tone("ruc_expiry_km")],
    ["Service", service, tone("next_service_date")],
  ];

  async function save() {
    if (busy) return;
    if (!navigator.onLine) { setErr("No signal - the truck's details go straight to the office. Try again when you have signal, or skip for now."); return; }
    setBusy(true); setErr("");
    const body = {};
    for (const [k] of [...HEADER_TEXT, ...HEADER_DATES]) body[k] = (draft[k] || "").trim() || null;
    for (const [k] of HEADER_KM) body[k] = (draft[k] || "").trim() || null;
    try {
      const res = await API.patch(`/trucks/${encodeURIComponent(truck.id)}/header`, body);
      const updated = res?.data && res.data.id ? res.data : { ...truck, ...body };
      onSaved && onSaved(updated);
      setEditing(false);
      setSavedNote(`Saved - thanks. You will only be asked again when something is due.`);
      try { localStorage.removeItem(snoozeKey(truck.id)); } catch { /* storage off */ }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not save");
    } finally {
      setBusy(false);
    }
  }
  function skip() {
    try { localStorage.setItem(snoozeKey(truck.id), String(Date.now() + SNOOZE_DAYS * 86400000)); } catch { /* storage off */ }
    setEditing(false);
  }

  const inputCls = "mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2.5 text-slate-100 text-sm outline-none focus:border-blue-500";
  if (editing) {
    return (
      <div className="mt-3 border-t border-slate-800 pt-3">
        <p className="text-sm font-semibold text-slate-100">Truck details</p>
        <p className="text-xs text-slate-400 mt-0.5">
          {dueLabels.length ? `${dueLabels.join(", ")} ${dueLabels.length > 1 ? "are" : "is"} due or expired - if it has been renewed, enter the new date. ` : ""}
          {missing.length ? `Missing: ${missing.join(", ")}. ` : ""}
          Read them off the labels on the windscreen. Leave anything you cannot see blank.
        </p>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {HEADER_TEXT.map(([k, label]) => (
            <label key={k} className="text-[11px] text-slate-400">
              {label}
              <input value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className={inputCls} maxLength={64} />
            </label>
          ))}
          {HEADER_DATES.map(([k, label]) => (
            <label key={k} className="text-[11px] text-slate-400">
              {label}
              <input type="date" value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className={inputCls} />
            </label>
          ))}
          {HEADER_KM.map(([k, label]) => (
            <label key={k} className="text-[11px] text-slate-400">
              {label}
              <input type="number" inputMode="numeric" min="1" step="100" value={draft[k] || ""} placeholder="e.g. 250000"
                     onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className={inputCls} />
            </label>
          ))}
          {truck.hubo_reading != null && (
            <p className="col-span-2 text-[11px] text-slate-500">
              Latest hubo on this truck: {Number(truck.hubo_reading).toLocaleString("en-NZ")} km
              {truck.hubo_date ? ` (${truck.hubo_date})` : ""}. RUC and the service are measured against it.
            </p>
          )}
        </div>
        {err && <p className="mt-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</p>}
        <div className="mt-3 flex gap-2">
          <button onClick={save} disabled={busy}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 py-2.5 rounded-xl text-sm font-bold">
            {busy && <Loader2 size={14} className="animate-spin" />} Save details
          </button>
          <button onClick={skip} disabled={busy}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-800 hover:bg-slate-700 disabled:opacity-50">
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-slate-800 pt-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        {rows.map(([label, value, cls]) => (
          <div key={label} className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-slate-500 shrink-0">{label}</span>
            <span className={`truncate text-right ${cls || (value === "—" ? "text-slate-600" : "text-slate-200")}`}>{value}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-emerald-300">{savedNote}</span>
        <button onClick={() => { setDraft(blank(truck.id)); setErr(""); setEditing(true); }}
                className="text-[11px] font-semibold text-blue-300 hover:text-blue-200">
          Edit details
        </button>
      </div>
    </div>
  );
}

function TruckPicker({ trucks, value, onPick }) {
  const rows = trucks
    .filter((t) => t.is_active !== false || String(t.id) === String(value))
    .slice()
    .sort((a, b) => String(a.license_plate || "").localeCompare(String(b.license_plate || "")));
  if (!rows.length) {
    return (
      <p className="text-sm text-slate-500 text-center py-6">
        No trucks on the fleet list yet - the office adds them under Settings.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      {rows.map((t) => {
        const active = String(t.id) === String(value);
        const expired = (t.expiry_warnings || []).some((w) => (w.days != null ? Number(w.days) < 0 : false));
        return (
          <button key={t.id} onClick={() => onPick(t.id)}
                  className={`text-left rounded-2xl border px-3 py-3 min-h-[4.25rem] active:scale-[0.99] transition ${
                    active ? "bg-blue-600/20 border-blue-500" : "bg-slate-900 border-slate-800"}`}>
            <p className="font-bold text-base leading-tight truncate">{t.license_plate || "(no plate)"}</p>
            <p className="text-[11px] text-slate-400 truncate mt-0.5">
              {[t.fleet_no ? `Fleet ${t.fleet_no}` : null, t.make].filter(Boolean).join(" · ") || " "}
            </p>
            {expired && <p className="text-[10px] text-red-300 font-semibold mt-0.5">expiry overdue</p>}
          </button>
        );
      })}
    </div>
  );
}

// One control per item: three equal targets, the chosen one lit. Fault is
// deliberately as easy to press as OK - a driver who has to hunt for it will
// press OK instead.
const STATES = [
  ["ok", "OK", Check, "bg-emerald-600 text-white border-emerald-500"],
  ["fault", "Fault", X, "bg-red-600 text-white border-red-500"],
  ["na", "N/A", Minus, "bg-slate-600 text-white border-slate-500"],
];

/* The note line opens with Fault and stays as long as it has text: a driver
 * who crosses Mirrors, writes "cracked left", then decides it is OK after
 * all keeps the note in view and it is still sent ("tyre wearing, still OK"
 * is exactly what the office wants to hear). Cleared, it folds away. */
function ItemRow({ item, value, note, onSet, onNote }) {
  const fault = value === "fault";
  const showNote = fault || !!note;
  return (
    <div className="py-1">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm leading-tight">
            {item.label}
            {item.critical && <span className="ml-1 text-[9px] uppercase tracking-wider text-red-400/80 font-bold align-middle">safety</span>}
          </p>
        </div>
        <div className="flex gap-1 shrink-0" role="radiogroup" aria-label={item.label}>
          {STATES.map(([key, label, Icon, lit]) => (
            <button key={key} onClick={() => onSet(item.key, key)} aria-pressed={value === key}
                    className={`w-[3.4rem] h-11 rounded-lg border text-[11px] font-bold flex flex-col items-center justify-center leading-none gap-0.5 ${
                      value === key ? lit : "bg-slate-900 border-slate-700 text-slate-400"}`}>
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
      </div>
      {showNote && (
        <input value={note || ""} onChange={(e) => onNote(item.key, e.target.value)} maxLength={ITEM_NOTE_MAX}
               placeholder={fault ? "What's wrong? e.g. cracked left mirror" : "Note for the office, e.g. wearing but still OK"}
               aria-label={`${item.label} - note`}
               className={`mt-1.5 w-full bg-slate-950 border rounded-lg px-3 py-2 text-sm focus:outline-none ${
                 fault ? "border-red-500/40 focus:border-red-400" : "border-slate-700 focus:border-blue-500"}`} />
      )}
    </div>
  );
}

function ItemBadge({ value }) {
  if (value === "ok") return <span className="text-emerald-400 text-xs font-bold flex items-center gap-1"><Check size={13} /> OK</span>;
  if (value === "fault") return <span className="text-red-300 text-xs font-bold flex items-center gap-1"><X size={13} /> Fault</span>;
  if (value === "na") return <span className="text-slate-400 text-xs font-bold flex items-center gap-1"><Minus size={13} /> N/A</span>;
  return <span className="text-slate-600 text-xs">—</span>;
}

function statusBadge(check) {
  if (check.failed) return ["bg-red-500/15 text-red-300 border-red-500/40", "Refused by the office"];
  if (check.status === "cleared") return ["bg-emerald-500/15 text-emerald-300 border-emerald-500/40", "Fault cleared"];
  if (check.status === "fault") return ["bg-red-500/15 text-red-300 border-red-500/40", "Fault reported"];
  return ["bg-emerald-500/15 text-emerald-300 border-emerald-500/40", "All OK"];
}

/* Today's check, read-only. A check is signed the moment it is submitted -
 * it is corrected by the office with an audit row, not re-ticked in the cab.
 * The one thing a driver may still do is ADD to it: a comment later in the
 * day ("mirror now taped up", "we need a torch in this cab") lands on the
 * sheet as a dated line, and the ticks stay as they were signed.
 * onChange(check) - the server's copy after a comment was added. */
function ExistingCheck({ check, template, onChange }) {
  const [cls, text] = statusBadge(check);
  const items = check.items || {};
  const notes = check.item_notes || {};
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [commentErr, setCommentErr] = useState("");
  // The office only has a check once it has sent; until then there is
  // nothing on the server for a comment to attach to.
  const canComment = !!check.id && !check.pending && !check.failed;

  async function addComment() {
    const body = comment.trim();
    if (!body || commentBusy) return;
    if (!navigator.onLine) {
      /* Deliberately NOT queued: the queue is for punches and checks, whose
       * loss costs a day's pay or a safety record. A comment that quietly
       * arrived hours later would read as if written then. */
      setCommentErr("No signal - a comment goes straight to the office, so try again when you have signal.");
      return;
    }
    setCommentBusy(true);
    setCommentErr("");
    try {
      const res = await API.post(`/checks/${check.id}/comment`, { comment: body });
      const fresh = res?.data?.check ?? res?.data;
      setComment("");
      setCommentOpen(false);
      onChange(fresh?.items ? fresh : { ...check, faults_note: [check.faults_note, body].filter(Boolean).join("\n") });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      setCommentErr(typeof detail === "string" && detail
        ? detail
        : status ? `The office could not add the comment (${status})` : "Could not reach the office - try again when you have signal.");
    } finally {
      setCommentBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>{text}</span>
          <span className="text-xs text-slate-400">
            today {check.submitted_nz || nzClock(check.submitted_at) || ""}
            {check.person_name ? ` · ${check.person_name}` : ""}
          </span>
        </div>
        {check.pending && (
          <p className="text-[11px] text-blue-300">Held on this phone - sends when you next open the app on signal. Comments can be added once it has sent.</p>
        )}
        {check.failed && (
          <p className="text-[11px] text-red-300">{check.failed} - ring the office so it is recorded.</p>
        )}
        <p className="text-sm">
          <span className="text-slate-400">Hubo</span>{" "}
          <span className="font-semibold tabular-nums">{check.hubo_reading != null ? check.hubo_reading : "not recorded"}</span>
        </p>
        {(check.faults || []).length > 0 && (
          <div className="text-sm text-red-200">
            <span className="text-slate-400">Faults</span>
            {check.faults.map((f) => {
              const note = f.note || notes[f.key];
              return (
                <span key={f.key} className="block pl-3">{f.label || f.key}{note ? ` - ${note}` : ""}</span>
              );
            })}
          </div>
        )}
        {check.faults_note && (
          <p className="text-sm text-slate-200 whitespace-pre-wrap">
            <span className="text-slate-400">Comments</span> {check.faults_note}
          </p>
        )}
        {check.action_taken && (
          <p className="text-sm text-emerald-200 flex gap-1.5">
            <Wrench size={14} className="mt-0.5 shrink-0" />
            <span>
              {check.action_taken}
              <span className="text-slate-400">
                {check.action_by ? ` · ${check.action_by}` : ""}{check.action_at_nz ? ` ${check.action_at_nz}` : ""}
              </span>
            </span>
          </p>
        )}
        {canComment && !commentOpen && (
          <button onClick={() => setCommentOpen(true)}
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold bg-slate-800 hover:bg-slate-700 px-3 py-2.5 rounded-lg">
            <MessageSquarePlus size={14} /> Add a comment or suggestion
          </button>
        )}
        {canComment && commentOpen && (
          <div className="space-y-1.5">
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={1000}
                      placeholder="e.g. mirror taped up for now · could we get a torch for this cab"
                      aria-label="Comment for the office"
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500" />
            {commentErr && (
              <p className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-2.5 py-1.5">{commentErr}</p>
            )}
            <div className="flex gap-2">
              <button onClick={addComment} disabled={commentBusy || !comment.trim()}
                      className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold bg-blue-600 hover:bg-blue-500 disabled:opacity-40 px-3 py-2.5 rounded-lg">
                {commentBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to the office
              </button>
              <button onClick={() => { setCommentOpen(false); setCommentErr(""); }} disabled={commentBusy}
                      className="text-xs font-semibold bg-slate-800 hover:bg-slate-700 disabled:opacity-40 px-3 py-2.5 rounded-lg">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {template.groups.map((g) => (
        <div key={g.key} className="bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">{g.label}</p>
          {(g.items || []).map((it) => (
            <div key={it.key} className="py-1 text-sm">
              <div className="flex items-center justify-between">
                <span className={items[it.key] === "fault" ? "text-red-200" : ""}>{it.label}</span>
                <ItemBadge value={items[it.key]} />
              </div>
              {notes[it.key] && (
                <p className={`text-xs whitespace-pre-wrap ${items[it.key] === "fault" ? "text-red-200/80" : "text-slate-400"}`}>
                  {notes[it.key]}
                </p>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * the screen
 * ------------------------------------------------------------------ */

/**
 * mode "start": submitting hands the payload back so DriverApp queues the
 *   check and then clocks in on the same truck. A check already done today
 *   on the chosen truck is shown instead of a second form, with Start shift.
 * mode "check": a check on its own - the Pre-check button. Read-only when
 *   today's is done, a form otherwise (a driver not on shift may do one).
 *
 * onDone({ payload, truck, faults, existing, pos }) - payload null when an
 * existing check was used. onClose() - cancelled, nothing punched.
 */
export default function PreCheck({ mode = "check", truckId: initialTruckId = "", onDone, onClose }) {
  const [trucks, setTrucks] = useState(() => cachedTrucks());
  const [template, setTemplate] = useState(() => cachedTemplate());
  const [truckId, setTruckId] = useState(() => initialTruckId || lastTruckId());
  const [picking, setPicking] = useState(() => !(initialTruckId || lastTruckId()));
  const [loadErr, setLoadErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [existing, setExisting] = useState(null);    // today's check on this truck, if done
  const [existingBusy, setExistingBusy] = useState(false);
  const [items, setItems] = useState({});
  const [itemNotes, setItemNotes] = useState({});    // {item_key: what's wrong} - sent only where typed
  const [hubo, setHubo] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const truck = trucks.find((t) => String(t.id) === String(truckId)) || null;
  const allItems = useMemo(() => template.groups.flatMap((g) => g.items || []), [template]);
  const total = allItems.length;
  const answered = allItems.filter((it) => items[it.key]).length;
  const faults = allItems.filter((it) => items[it.key] === "fault");
  const complete = total > 0 && answered === total;

  // A header the driver just saved replaces the truck in the list AND in the
  // offline cache, so the next open in the yard shows what they entered.
  const onTruckSaved = useCallback((updated) => {
    setTrucks((prev) => {
      const next = prev.map((t) => (String(t.id) === String(updated.id) ? { ...t, ...updated } : t));
      writeJson(TRUCKS_KEY, next);
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!navigator.onLine) {
      if (!cachedTrucks().length) setLoadErr("No signal and no saved truck list - open the app on signal once, then try again");
      return;
    }
    setRefreshing(true);
    setLoadErr("");
    const fresh = await warmPreCheckCache(API);
    setTrucks(fresh.trucks);
    setTemplate(fresh.template);
    if (!fresh.trucks.length) setLoadErr("Could not load the truck list - try again when you have signal");
    setRefreshing(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  /* Is today's check on this truck already done? The phone's own copy wins
   * while it is still queued (the office cannot know about it yet); otherwise
   * the office is asked, and with no signal the phone's copy is the answer. */
  useEffect(() => {
    if (!truckId) { setExisting(null); return undefined; }
    let cancelled = false;
    const local = cachedShift()?.check;
    const localMatch = local && String(local.truck_id) === String(truckId) && local.check_date === deviceDate()
      ? local : null;
    if (localMatch?.pending) {
      setExisting(localMatch);
      return undefined;
    }
    (async () => {
      setExistingBusy(true);
      let found = null;
      try {
        const res = await API.get("/checks/today", { params: { truck_id: truckId } });
        if (res?.data?.done && res.data.check) {
          found = { ...res.data.check, pending: false };
          /* Keep the phone's copy in step with the office's: a fault the
           * office has since cleared, or a comment it added, must reach the
           * amber card on the shift bar too. No-op unless the cache holds
           * this very check - a sheet merely looked at is not remembered. */
          replaceCachedCheck(found);
        } else if (res?.data?.done === false && localMatch?.id && !localMatch.failed) {
          /* The office has the last word: a copy this phone holds that the
           * server no longer counts (voided at the office) must go, or the
           * fault card would stay up and the form would never reopen. */
          forgetCachedCheck(localMatch);
        }
      } catch {
        found = localMatch;
      }
      if (!cancelled) { setExisting(found); setExistingBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [truckId]);

  function pickTruck(id) {
    setTruckId(id);
    setPicking(false);
    setItems({});            // the answers were about the other truck
    setItemNotes({});
    try { localStorage.setItem(LAST_TRUCK_KEY, String(id)); } catch { /* storage off */ }
  }

  const setItem = (key, value) => setItems((prev) => ({ ...prev, [key]: value }));
  const setItemNote = (key, value) => setItemNotes((prev) => ({ ...prev, [key]: value }));

  /* A comment was added to today's sheet: show the server's copy, and keep
   * the phone's cached copy in step so the fault card does not disagree. */
  function checkChanged(fresh) {
    const next = { ...fresh, pending: false };
    setExisting(next);
    replaceCachedCheck(next);
  }

  /* Only UNSET items - a Fault already ticked is never overwritten by the
   * shortcut, so "tick the one fault, then All OK" is safe in that order. */
  function allOk(keys) {
    setItems((prev) => {
      const next = { ...prev };
      keys.forEach((k) => { if (!next[k]) next[k] = "ok"; });
      return next;
    });
  }

  async function submit() {
    if (!truck || !complete || submitting) return;
    const huboText = hubo.trim();
    const huboNum = huboText === "" ? null : Number.parseInt(huboText, 10);
    // only the notes with something in them, only for items on the sheet
    const notesToSend = Object.fromEntries(allItems
      .map((it) => [it.key, (itemNotes[it.key] || "").trim().slice(0, ITEM_NOTE_MAX)])
      .filter(([, text]) => text));
    const nags = [];
    if (huboNum == null || !Number.isFinite(huboNum)) nags.push("No hubo reading entered.");
    // a fault explained under its own item needs no second explanation
    const unexplained = faults.filter((it) => !notesToSend[it.key]);
    if (unexplained.length && !note.trim()) nags.push(`${unexplained.length} fault${unexplained.length > 1 ? "s" : ""} ticked with no comment for the office.`);
    if (nags.length && !window.confirm(`${nags.join("\n")}\n\nSubmit the check anyway?`)) return;

    setSubmitting(true);
    const pos = await getPosition();
    const payload = {
      truck_id: truck.id,
      hubo_reading: Number.isFinite(huboNum) ? huboNum : null,
      // exactly the template's keys - a stray key from an older cache would
      // be refused with the whole check
      items: Object.fromEntries(allItems.map((it) => [it.key, items[it.key]])),
      faults_note: note.trim(),
      item_notes: notesToSend,
    };
    if (pos.lat != null) { payload.lat = pos.lat; payload.lon = pos.lon; }
    onDone({
      payload, truck, existing: null, pos,
      faults: faults.map((it) => ({ key: it.key, label: it.label, note: notesToSend[it.key] || null })),
    });
  }

  function useExisting() {
    onDone({ payload: null, truck, existing, pos: {}, faults: existing?.faults || [] });
  }

  const starting = mode === "start";
  const showForm = !!truck && !picking && !existingBusy && !existing;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 text-slate-100 flex flex-col">
      {/* header */}
      <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
          <ClipboardCheck size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-bold leading-tight">Pre-check</h2>
          <p className="text-xs text-slate-400 truncate">
            {truck && !picking ? truckTitle(truck) : "Daily checklist · goods service vehicle"}
          </p>
        </div>
        <button onClick={refresh} disabled={refreshing} className="p-2 rounded-lg hover:bg-slate-800" aria-label="Refresh">
          <RefreshCw size={17} className={refreshing ? "animate-spin" : ""} />
        </button>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800" aria-label="Close">
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {loadErr && (
          <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">{loadErr}</p>
        )}

        {/* ---- which truck ---- */}
        {(picking || !truck) ? (
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 flex items-center gap-1">
              <Truck size={13} /> Which truck are you taking?
            </p>
            {refreshing && !trucks.length
              ? <Loader2 className="animate-spin mx-auto my-6 text-slate-500" />
              : <TruckPicker trucks={trucks} value={truckId} onPick={pickTruck} />}
          </div>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <Truck size={20} className="text-blue-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-lg leading-tight">{truck.license_plate || "(no plate)"}</p>
                <p className="text-xs text-slate-400 truncate">
                  {[truck.fleet_no ? `Fleet ${truck.fleet_no}` : null, truck.make].filter(Boolean).join(" · ") || "No fleet details on file"}
                </p>
              </div>
              <button onClick={() => setPicking(true)}
                      className="shrink-0 text-xs font-semibold bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-lg">
                Change
              </button>
            </div>
            <ExpiryWarnings truck={truck} />
            <VehicleHeader truck={truck} onSaved={onTruckSaved} />
          </div>
        )}

        {truck && !picking && existingBusy && (
          <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
            <Loader2 className="animate-spin mr-2" size={16} /> Checking today's record…
          </div>
        )}

        {/* ---- already done today ---- */}
        {truck && !picking && !existingBusy && existing && (
          <ExistingCheck check={existing} template={template} onChange={checkChanged} />
        )}

        {/* ---- the form ---- */}
        {showForm && (
          <>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
              <label className="block text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1.5" htmlFor="precheck-hubo">
                Hubo reading
              </label>
              <input id="precheck-hubo" type="number" inputMode="numeric" pattern="[0-9]*" min="0"
                     value={hubo} onChange={(e) => setHubo(e.target.value)} placeholder="e.g. 412873"
                     className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-3 text-lg font-semibold tabular-nums focus:outline-none focus:border-blue-500" />
            </div>

            {answered < total && (
              <button onClick={() => allOk(allItems.map((it) => it.key))}
                      className="w-full flex items-center justify-center gap-2 bg-emerald-600/20 border border-emerald-500/50 text-emerald-200 hover:bg-emerald-600/30 py-3.5 rounded-xl font-bold">
                <Check size={18} /> All remaining OK ({total - answered})
              </button>
            )}

            {template.groups.map((g) => {
              const keys = (g.items || []).map((it) => it.key);
              const left = keys.filter((k) => !items[k]).length;
              return (
                <div key={g.key} className="bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="flex-1 text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                      {g.label} <span className="normal-case tracking-normal font-normal text-slate-500">· {keys.length - left}/{keys.length}</span>
                    </p>
                    {left > 0 && (
                      <button onClick={() => allOk(keys)}
                              className="text-[11px] font-semibold text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 px-2.5 py-1.5 rounded-lg">
                        All OK
                      </button>
                    )}
                  </div>
                  {(g.items || []).map((it) => (
                    <ItemRow key={it.key} item={it} value={items[it.key]} note={itemNotes[it.key]}
                             onSet={setItem} onNote={setItemNote} />
                  ))}
                </div>
              );
            })}

            {/* Always here, faults or not: the paper line is "Faults/Comments"
                and the operator wants suggestions on it too. Red only while
                something is crossed, so the eye goes to it when it matters. */}
            <div className={`rounded-2xl p-4 border ${
              faults.length ? "bg-red-500/10 border-red-500/30" : "bg-slate-900 border-slate-800"}`}>
              <label htmlFor="precheck-note"
                     className={`text-[11px] uppercase tracking-wider font-bold mb-1 flex items-center gap-1 ${
                       faults.length ? "text-red-300" : "text-slate-400"}`}>
                {faults.length ? <AlertTriangle size={13} /> : <MessageSquare size={13} />} Faults, comments or suggestions
              </label>
              {faults.length > 0 && (
                <p className="text-xs text-red-200/80 mb-2">
                  {faults.map((it) => it.label).join(", ")} - tell the office before driving.
                </p>
              )}
              <textarea id="precheck-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                        placeholder="Anything for the office - a fault, something wearing, or an idea (e.g. new tie-down straps needed)"
                        className={`w-full bg-slate-950 border rounded-xl px-3 py-2.5 text-sm focus:outline-none ${
                          faults.length ? "border-red-500/30 focus:border-red-400" : "border-slate-700 focus:border-blue-500"}`} />
              <p className="mt-1.5 text-[11px] text-slate-500">
                Not just for faults - requests and ideas are read too. You can add to it later today from this screen.
              </p>
            </div>
          </>
        )}
      </div>

      {/* footer */}
      <div className="border-t border-slate-800 bg-slate-900/95 backdrop-blur px-4 pt-2.5 pb-3">
        {truck && !picking && existing ? (
          <>
            {starting ? (
              <button onClick={useExisting} disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 py-3.5 rounded-xl font-bold">
                <Check size={18} /> Start shift on {truck.license_plate || "this truck"}
              </button>
            ) : (
              <button onClick={onClose}
                      className="w-full bg-slate-800 hover:bg-slate-700 py-3.5 rounded-xl font-bold">
                Done
              </button>
            )}
            <p className="mt-1.5 text-center text-[11px] text-slate-500">
              Already checked today. Different truck? Press Change.
            </p>
          </>
        ) : (
          <>
            <button onClick={submit} disabled={!showForm || !complete || submitting}
                    className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 py-3.5 rounded-xl font-bold">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <ClipboardCheck size={18} />}
              {starting ? "Submit & start shift" : "Submit pre-check"}
            </button>
            {/* The operator's call: the shift may start first and the sheet be
                done once the truck is loaded. The shift still carries the truck,
                and the Pre-check button stays amber until the sheet is in. */}
            {starting && truck && !picking && (
              <button
                onClick={() => onDone({ payload: null, truck, existing: null, pos: {}, faults: [], skipCheck: true })}
                disabled={submitting}
                className="w-full mt-2 flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 py-3 rounded-xl text-sm font-semibold text-slate-200">
                <Play size={15} /> Start shift now on {truck.license_plate || "this truck"} — pre-check after
              </button>
            )}
            <p className="mt-1.5 text-center text-[11px] text-slate-500">
              {!truck || picking
                ? "Pick the truck first"
                : complete
                  ? (faults.length ? `${faults.length} fault${faults.length > 1 ? "s" : ""} - recorded and sent to the office, nothing is blocked` : `All ${total} items answered`)
                  : `${answered} of ${total} items answered`}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
